<script lang="ts" setup>
import { faBracketsCurly } from "@fortawesome/pro-light-svg-icons";
import { faPlus, faSearch } from "@fortawesome/pro-regular-svg-icons";
import type { FieldDefinition } from "~/models/Automation";
import Fuse from "fuse.js";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { AutomationSideCard } from "#components";

const props = defineProps<{
    asDiv?: boolean;
    noCopy?: boolean;
    editable?: FieldDefinition | undefined;
    deletable?: FieldDefinition | undefined;
    creatable?: boolean;
    fields: FieldDefinition[];
    withValues?: boolean;
    input?: any;
}>();

const show = defineModel<boolean>("show", { required: true, default: false });

const emit = defineEmits<{
    (e: "saved", v: { to: FieldDefinition; from: FieldDefinition }): void;
    (e: "update:editable", v: FieldDefinition | undefined): void;
    (e: "update:deletable", v: FieldDefinition | undefined): void;
    (e: "created", field: FieldDefinition): void;
}>();

const search = ref<string>("");
const debouncedSearch = refDebounced(search, 250);

const filtered_fields = computed(() => {
    let fields = props.fields.filter(
        (field) => field && ![null, "null"].includes(field.type),
    );

    if (props.withValues) {
        fields = fields.filter((field) => {
            if (!field.default_value) return false;
            if (
                Array.isArray(field.default_value) &&
                field.default_value.length === 0
            )
                return false;

            return true;
        });
    }

    if (!debouncedSearch.value) return fields;

    const fuse = new Fuse(fields, {
        shouldSort: true,
        minMatchCharLength: 2,
        keys: [
            "label",
            "key",
            "options.label",
            "options.value",
            "default_value",
        ],
        threshold: 0.5,
        useExtendedSearch: true,
        ignoreLocation: true,
    });

    const result = fuse
        .search(debouncedSearch.value ?? "")
        .map(({ item, ...rest }) => {
            return item;
        });

    if (!result.length) {
        return fields.filter((field) => {
            if (field.key.includes(debouncedSearch.value ?? "")) {
                return true;
            }

            if (field.label.includes(debouncedSearch.value ?? "")) {
                return true;
            }

            if (
                field.options?.find(
                    (option) =>
                        option.label.includes(debouncedSearch.value ?? "") ||
                        (typeof option.value === "string" &&
                            option.value.includes(debouncedSearch.value ?? "")),
                )
            ) {
                return true;
            }

            return false;
        });
    }

    return result;
});

const loaded_count = ref<number>(30);
const loaded_fields = computed(() => {
    return [...filtered_fields.value].splice(0, loaded_count.value);
});

watch(search, () => (loaded_count.value = 30));

const new_field = ref<FieldDefinition>({
    key: "",
    type: "string",
    sub_type: null,
    label: "",
    default_value: "",
} as FieldDefinition);

function finishNewField(): FieldDefinition {
    return createFieldCollection().add(useCloneDeep(new_field.value))
        .items[0] as FieldDefinition;
}

function onCreated(close: () => void) {
    if (new_field.value) {
        emit("created", finishNewField());
    }

    onResetCreate(close);
}

function onLoadMore() {
    if (loaded_count.value < filtered_fields.value.length) {
        const new_count = loaded_count.value + 30;
        loaded_count.value =
            new_count > filtered_fields.value.length
                ? filtered_fields.value.length
                : new_count;
    }
}

const dataList = ref(null);
useInfiniteScroll(dataList, onLoadMore);

function onResetCreate(close: () => void) {
    close();
    new_field.value = {
        key: "",
        type: "string",
        sub_type: null,
        label: "",
        default_value: "",
    } as FieldDefinition;
    emit("update:editable", undefined);
    emit("update:deletable", undefined);
}

function onSaved(field: { to: FieldDefinition; from: FieldDefinition }) {
    emit("saved", field);
}
</script>

<template>
    <NuxtErrorBoundary>
        <template #error="{ error }">
            <UAlert color="red" :title="error.message" />
        </template>
        <component
            :is="asDiv ? 'div' : AutomationSideCard"
            v-if="show"
            :class="
                asDiv
                    ? undefined
                    : 'w-[28rem] overflow-hidden max-h-full flex flex-col'
            "
            :on-close="() => $emit('update:show', false)"
            closeable
        >
            <template v-if="!asdiv" #title>
                <FontAwesomeIcon :icon="faBracketsCurly" class="mr-2" />
                Variables
            </template>
            <UInput
                v-model="search"
                :placeholder="'Search'"
                :ui="{ icon: { trailing: { pointer: '' } } }"
                class="px-0 mb-2"
                size="lg"
                v-bind="input"
            >
                <template #leading>
                    <FontAwesomeIcon :icon="faSearch" />
                </template>
                <template v-if="creatable" #trailing>
                    <UPopover>
                        <UButton color="white" square variant="ghost">
                            <FontAwesomeIcon :icon="faPlus" />
                        </UButton>
                        <template #panel="{ close }">
                            <div class="p-4">
                                <h3 class="font-display text-lg mb-4">
                                    New variable
                                </h3>
                                <CreateFieldDefinition
                                    v-model:field="new_field"
                                    :fields="fields"
                                    can-choose-type
                                    @cancelled="onResetCreate(close)"
                                    @saved="onCreated(close)"
                                />
                            </div>
                        </template>
                    </UPopover>
                </template>
            </UInput>
            <AppFieldDataList
                ref="dataList"
                :editable="editable"
                @update:editable="emit('update:editable', $event)"
                :deletable="deletable"
                @update:deletable="emit('update:deletable', $event)"
                :fields="loaded_fields"
                :no-copy="noCopy"
                :with-values="withValues"
                class="flex-1 max-h-[calc(100%-3rem)] min-h-0 overflow-y-auto"
                @saved="onSaved"
            />
        </component>
    </NuxtErrorBoundary>
</template>
